import { RadioGroup } from "@headlessui/react";
import { t } from "@/i18n-js/instance";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { planSettingsPath } from "@circle-react/helpers/urlHelpers";
import { Icon } from "@circle-react-shared/Icon";
import { Button } from "@circle-react-uikit/Button";
import { RadioCard } from "@circle-react-uikit/RadioCard";
import { i18nRoot } from "../../constants";
import { TranscriptFileUploadInput } from "../TranscriptFileUploadInput";
import { isWebVttValid } from "./utils";

interface TranscriptFileTypeSelectComponentProps {
  aiFileDownloadUrl?: string;
  onChange: (value: string) => void;
  supportsAi: boolean;
  value: string;
}

export const TranscriptFileTypeSelectComponent = ({
  onChange,
  value,
  supportsAi,
  aiFileDownloadUrl,
}: TranscriptFileTypeSelectComponentProps) => {
  const validateVttFile = (vttFileValue: string) => {
    if (!isWebVttValid(vttFileValue)) {
      return t(`${i18nRoot}.transcript.file_type.user_file_error`);
    }
    return true;
  };

  const handleUpgradeClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    window.open(planSettingsPath(), "_blank");
  };

  const handleAiFileDownloadClick = (
    e: React.MouseEvent<HTMLButtonElement>,
  ) => {
    e.preventDefault();
    window.open(aiFileDownloadUrl, "_blank");
  };

  const shouldDisplayUploadButton = value === "user";
  const shouldDisplayUpgradeButton =
    value === "ai" && !supportsAi && !aiFileDownloadUrl;
  const shouldDisplayAiFileDownloadButton =
    value === "ai" && !!aiFileDownloadUrl;

  return (
    <RadioGroup
      className="w-full"
      name="file_type"
      value={value}
      onChange={onChange}
    >
      <div
        className={classNames("flex flex-col items-stretch gap-3", {
          "flex-col-reverse": !supportsAi,
        })}
      >
        <RadioCard className="py-4" value="ai">
          <RadioGroup.Label
            as="span"
            className="text-darkest block text-sm font-medium"
          >
            {t(`${i18nRoot}.transcript.file_type.ai`)}
          </RadioGroup.Label>
          <RadioGroup.Description
            as="span"
            className="text-default mt-1 flex flex-col justify-center gap-1 text-sm"
          >
            {t(`${i18nRoot}.transcript.file_type.ai_description`)}
            {shouldDisplayUpgradeButton && (
              <Button variant="circle" onClick={handleUpgradeClick}>
                {t(`${i18nRoot}.transcript.file_type.upgrade_label`)}
              </Button>
            )}
            {shouldDisplayAiFileDownloadButton && (
              <Button variant="secondary" onClick={handleAiFileDownloadClick}>
                <Icon type="16-download" className="mr-1.5" size={16} />
                {t(`${i18nRoot}.transcript.file_type.download_vtt_label`)}
              </Button>
            )}
          </RadioGroup.Description>
        </RadioCard>
        <RadioCard className="py-4" value="user">
          <RadioGroup.Label
            as="span"
            className="text-darkest block text-sm font-medium"
          >
            {t(`${i18nRoot}.transcript.file_type.user`)}
          </RadioGroup.Label>
          <RadioGroup.Description
            as="div"
            className="text-default mt-1 flex flex-col justify-center gap-1 text-sm"
          >
            {t(`${i18nRoot}.transcript.file_type.user_description`)}
            {shouldDisplayUploadButton && (
              <TranscriptFileUploadInput
                name="transcript.user_webvtt_file"
                rules={{ validate: validateVttFile }}
                placeholder={t(`${i18nRoot}.transcript.file_type.user_file`)}
              />
            )}
          </RadioGroup.Description>
        </RadioCard>
      </div>
    </RadioGroup>
  );
};
